import type {
  PlanFilter,
  SubscriptionPlan,
  Tier,
  IAPChangePlanState,
  AppleSubscriptionResponse,
  IAPProduct,
  ChangePlanSuccessResponse,
} from '@Panels/change-plan-modal/change-plan-modal.types';
import {CurrencyCode} from '@Utils/currency.util';
import {openErrorModal} from '@Modals/error-modal';

const getDefaultPlan = (): SubscriptionPlan => {
  return {
    id: 0,
    price: {
      id: 0,
      amount: 0,
      currencyCode: CurrencyCode.USD,
    },
    tiers: [],
    isPremiumPlus: false,
    duration: 1,
    name: '',
    level: 1,
  };
};

const getDefaultTier = (amount: number): Tier => {
  return {
    id: 0,
    startingUnit: 1,
    endingUnit: 1,
    perUnitAmount: 0,
    flatRateAmount: amount,
    displayPrice: amount,
  };
};

export const isPremium = (level: number): boolean => {
  return level === PlanLevels.PREMIUM.valueOf();
};

export const isPremiumPlus = (level: number): boolean => {
  return level === PlanLevels.PREMIUM_PLUS.valueOf();
};

export const isFree = (level: number): boolean => {
  return level === PlanLevels.FREE.valueOf();
};

export const getPlanName = (level: number): string => {
  if (isFree(level)) {
    return window.i18next.t('pmwjs_free');
  }

  return isPremium(level) ? window.i18next.t('pmwjs_premium') : window.i18next.t('pmwjs_premium_plus');
};

export const getDurationName = (duration: number): string => {
  if (isQuarterly(duration)) {
    return window.i18next.t('pmwjs_quarterly');
  }

  return isMonthly(duration) ? window.i18next.t('pmwjs_monthly') : window.i18next.t('pmwjs_yearly');
};

export const getTierName = (plan: SubscriptionPlan, tierId: number | null, selectedSeats: number): string => {
  if (tierId === null) {
    return '';
  }

  const tier = plan.tiers.find((planTier) => planTier.id === tierId);
  if (tier === undefined) {
    return '';
  }

  if (tier.endingUnit === null) {
    return window.i18next.t('pmwjs_x_seats', {seats: selectedSeats});
  }

  if (tier.startingUnit === tier.endingUnit) {
    return window.i18next.t('pmwjs_1_seat');
  }

  return window.i18next.t('pmwjs_x_seats', {seats: tier.endingUnit});
};

export const isMonthly = (duration: number): boolean => {
  return duration === PlanDurations.MONTHLY.valueOf();
};

export const isQuarterly = (duration: number): boolean => {
  return duration === PlanDurations.QUARTERLY.valueOf();
};

export enum PlanLevels {
  FREE = 0,
  PREMIUM = 1,
  PREMIUM_PLUS = 2,
}

export enum PlanDurations {
  MONTHLY = 1,
  QUARTERLY = 3,
  YEARLY = 12,
}

export enum PanelNames {
  CHANGE_PLAN = 'change-plan',
  REMOVE_TEAM_MEMBERS = 'remove-team-members',
  SUMMARY = 'summary',
}

export const getFilteredPlan = (plans: Array<SubscriptionPlan>, filter: PlanFilter): SubscriptionPlan => {
  for (let i = 0; i < plans.length; i++) {
    if (plans[i].level === filter.level && plans[i].duration === filter.duration) {
      return plans[i];
    }
  }

  return getDefaultPlan();
};

export const getCurrentlySelectedTier = (plan: SubscriptionPlan, selectedTierId: number | null): Tier | null => {
  if (selectedTierId === null) {
    return null;
  }

  const {tiers, price} = plan;
  for (let i = 0; i < tiers.length; i++) {
    if (tiers[i].id === selectedTierId) {
      return tiers[i];
    }
  }

  return getDefaultTier(price.amount);
};

export const getSeatsToRemoveForSwitchingToTier = (currentSeats: number, tier: Tier | null): number => {
  if (tier === null || tier.endingUnit === null) {
    return 0;
  }

  return Math.max(currentSeats - tier.endingUnit, 0);
};

export const getTierForSeatsForPlan = (plan: SubscriptionPlan, seats: number): Tier => {
  const {tiers, price} = plan;

  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];

    if (seats >= tier.startingUnit) {
      if (tier.endingUnit !== null && seats <= tier.endingUnit) {
        return tier;
      }

      if (tier.endingUnit === null) {
        return tier;
      }
    }
  }

  return getDefaultTier(price.amount);
};

export const getPlanDisplayPrice = (tier: Tier | null, currentDisplayPrice: number, plan: SubscriptionPlan, selectedSeats: number): number => {
  if (tier === null) {
    return currentDisplayPrice;
  }

  if (tier.endingUnit !== null) {
    return tier.displayPrice;
  }

  const tierForSelectedSeats = getTierForSeatsForPlan(plan, selectedSeats);
  if (tierForSelectedSeats.endingUnit !== null) {
    return tierForSelectedSeats.displayPrice;
  }

  return tierForSelectedSeats.perUnitAmount * selectedSeats;
};

export const onChangePlanSuccess = ({ga4PurchaseEventParams, redirectUrl}: ChangePlanSuccessResponse): void => {
  if (ga4PurchaseEventParams !== undefined) {
    window.PMW.gtm.trackGA4PurchaseEvent(ga4PurchaseEventParams);
  }
  window.location.href = redirectUrl;
};

const onSuccessfulIAP = async (transaction: {idTransaction: string}): Promise<void> => {
  try {
    const response = (await window.PMW.pollingAjaxCallAsync(
      async () => {},
      function () {
        return window.PMW.readLocal('premium/hasSuccessfulIAPTransactionProcessed', {idTransaction: transaction.idTransaction});
      }
    )) as ChangePlanSuccessResponse;
    if (response) {
      onChangePlanSuccess({
        redirectUrl: 'subscriptions?cp=success',
        ga4PurchaseEventParams: response.ga4PurchaseEventParams,
      });
    }
  } catch (e) {
    window.location.href = window.PMW.util.site_url('premium/subscriptions?cp=error');
  }
};

export const beginChangePlanForIOSApp = (productId: string, cancelChangePlan: () => void): void => {
  if (!productId) {
    cancelChangePlan();
    openErrorModal({log: false});
  }
  window.PMW.callBackForIAPurchase(
    productId,
    productId,
    window.PMW.IAP_PRODUCT_TYPES.SUBSCRIPTIONS_UPGRADE,
    (transaction: {idTransaction: string}) => {
      void onSuccessfulIAP(transaction);
    },
    (showError: boolean) => {
      if (showError) {
        openErrorModal({log: false});
      } else {
        cancelChangePlan();
      }
    }
  );
};

export const getIAPProducts = async (products: IAPProduct[], tiers: Tier[]): Promise<IAPChangePlanState> => {
  const transformedMap: Record<string, SubscriptionPlan> = {};
  const tierToIAPProductMap: Record<string, string> = {};

  products.forEach((product, i) => {
    const key = `${product.level}-${product.duration}`;
    const {tierId, duration, level, price, name, index} = product;
    if (!transformedMap[key]) {
      transformedMap[key] = {
        duration,
        id: i,
        isPremiumPlus: level === 2,
        level,
        name,
        price: {
          amount: price,
          currencyCode: product.currencyCode,
          id: i,
        },
        tiers: [],
      };
    }

    transformedMap[key].tiers[index] = {
      displayPrice: price,
      endingUnit: tiers[index].endingUnit,
      flatRateAmount: price,
      id: tierId,
      perUnitAmount: 0,
      startingUnit: tiers[index].startingUnit,
    };

    const mapKey = `${level}-${duration}-${tierId}`;
    tierToIAPProductMap[mapKey] = product.id;
  });

  const result = (await window.PMW.readLocal('premium/getUserAppleSubscription')) as AppleSubscriptionResponse;
  return {
    plans: Object.values(transformedMap),
    selectedDuration: result.currentDuration,
    selectedLevel: result.currentLevel,
    selectedSeats: result.endingUnit,
    selectedTierId: result.currentTierId,
    currentTierId: result.currentTierId,
    iapProducts: tierToIAPProductMap,
  };
};
